import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { selection } from '@/lib/utils/haptics'
import {
  BellRing,
  CheckCircle,
  Calendar,
  MessageCircle,
  Car,
  XCircle,
  DollarSign,
  Tag,
  Mail,
  Smartphone,
} from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { ToggleTrack } from '@/components/ui/ToggleTrack'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useSemanticColors, RADIUS, SPACING } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { FONTS, COLORS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useNotificationPrefs } from '@/lib/hooks/use-notification-prefs'
import { notificationService } from '@/lib/services/notification.service'
import { useAuth } from '@/lib/hooks/use-auth'
import { toast } from '@/lib/toast'
import { useState } from 'react'
import { ActivityIndicator } from 'react-native'

type LucideIcon = typeof BellRing

function SectionCard({
  children,
  colors,
}: {
  children: React.ReactNode
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View
      style={{
        marginHorizontal: SPACING.md,
        marginTop: SPACING.md,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        ...WEB_SHADOWS.sm,
      }}
    >
      {children}
    </View>
  )
}

function SectionTitle({
  title,
  colors,
}: {
  title: string
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <Text
      style={{
        fontSize: 12,
        color: colors.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontFamily: FONTS.body,
        paddingHorizontal: SPACING.md,
        marginTop: SPACING.lg,
        marginBottom: SPACING.xs,
      }}
    >
      {title}
    </Text>
  )
}

function Divider({ colors }: { colors: ReturnType<typeof useSemanticColors> }) {
  return (
    <View style={{ height: 1, backgroundColor: colors.border, marginHorizontal: SPACING.md }} />
  )
}

function ToggleRow({
  icon: Icon,
  label,
  value,
  onToggle,
  disabled,
  colors,
  isDark,
  testID,
}: {
  icon: LucideIcon
  label: string
  value: boolean
  onToggle: (v: boolean) => void
  disabled?: boolean
  colors: ReturnType<typeof useSemanticColors>
  isDark: boolean
  testID: string
}) {
  // The whole row is the control, the way iOS Settings works. React Native's
  // Switch renders 40x20 on web and is the only thing that takes a tap, so a
  // 44pt row had a 20pt target sitting in the middle of it — and there are nine
  // of them on this screen. The Switch is now painted, not pressed: taps fall
  // through to the row, and it is hidden from the accessibility tree so the row
  // is announced once rather than twice.
  return (
    <Pressable
      onPress={() => {
        if (disabled) return
        onToggle(!value)
        void selection()
      }}
      disabled={disabled}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
      accessibilityLabel={label}
      testID={testID}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: SPACING.md - RADIUS.xs,
        paddingHorizontal: SPACING.md,
        minHeight: 44,
        // 0.4 dropped the label under readable contrast, so a disabled row read
        // as broken rather than as "not available yet". 0.55 keeps it legible —
        // same value VerificationStepCard uses for its locked state.
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Icon size={20} color={colors.textMuted} strokeWidth={1.8} />
      <Text
        style={{
          flex: 1,
          marginLeft: SPACING.md,
          fontSize: 16,
          color: colors.textPrimary,
          fontFamily: FONTS.body,
        }}
      >
        {label}
      </Text>
      {/* Pixels only. React Native's Switch renders a real <input> on web that
          stays focusable and unnamed no matter which hiding prop is passed —
          aria-hidden, accessibilityElementsHidden and pointerEvents were all
          measured and none of them reach it. */}
      <ToggleTrack value={value} disabled={disabled} isDark={isDark} />
    </Pressable>
  )
}

export default function NotificationsPrefsScreen() {
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { user } = useAuth()
  const [isTestingPush, setIsTestingPush] = useState(false)

  const { prefs, togglePref } = useNotificationPrefs()
  const pushOff = !prefs.pushEnabled

  return (
    <ScreenContainer scroll={false}>
      {/* Not `notification_prefs.title` — that is the exact word the inbox
          screen puts in the same slot, so tapping the gear icon changed the
          screen without changing the header. */}
      <ScreenHeader title={t('common.preferences')} />
      <ScrollView
        testID="notifications.prefs-list"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: insets.bottom + SPACING.lg }}
      >
        <SectionTitle title={t('notification_prefs.section_general')} colors={colors} />
        <SectionCard colors={colors}>
          <ToggleRow
            icon={BellRing}
            label={t('notification_prefs.push_enabled')}
            value={prefs.pushEnabled}
            onToggle={() => togglePref('pushEnabled')}
            colors={colors}
            isDark={isDark}
            testID="notifications.pushEnabled-toggle"
          />
        </SectionCard>

        {/* Seven rows below grey out when push is off. Saying why once, here,
            is what VerificationStepCard does with `lockedReason` — without it
            the user sees dead controls and no cause. */}
        {pushOff ? (
          <Text
            testID="notifications.push-off-notice"
            style={{
              marginHorizontal: SPACING.md,
              marginTop: SPACING.sm,
              fontSize: 13,
              lineHeight: 18,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('notification_prefs.push_off_notice')}
          </Text>
        ) : null}

        <SectionTitle title={t('notification_prefs.section_trips')} colors={colors} />
        <SectionCard colors={colors}>
          <ToggleRow
            icon={CheckCircle}
            label={t('notification_prefs.booking_updates')}
            value={prefs.pushBookingUpdates}
            onToggle={() => togglePref('pushBookingUpdates')}
            disabled={pushOff}
            colors={colors}
            isDark={isDark}
            testID="notifications.bookingUpdates-toggle"
          />
          <Divider colors={colors} />
          <ToggleRow
            icon={Calendar}
            label={t('notification_prefs.trip_reminders')}
            value={prefs.pushTripReminders}
            onToggle={() => togglePref('pushTripReminders')}
            disabled={pushOff}
            colors={colors}
            isDark={isDark}
            testID="notifications.tripReminders-toggle"
          />
        </SectionCard>

        <SectionTitle title={t('notification_prefs.section_messages')} colors={colors} />
        <SectionCard colors={colors}>
          <ToggleRow
            icon={MessageCircle}
            label={t('notification_prefs.new_messages')}
            value={prefs.pushNewMessages}
            onToggle={() => togglePref('pushNewMessages')}
            disabled={pushOff}
            colors={colors}
            isDark={isDark}
            testID="notifications.newMessages-toggle"
          />
        </SectionCard>

        <SectionTitle title={t('notification_prefs.section_hosting')} colors={colors} />
        <SectionCard colors={colors}>
          <ToggleRow
            icon={Car}
            label={t('notification_prefs.host_bookings')}
            value={prefs.pushHostBookings}
            onToggle={() => togglePref('pushHostBookings')}
            disabled={pushOff}
            colors={colors}
            isDark={isDark}
            testID="notifications.hostBookings-toggle"
          />
          <Divider colors={colors} />
          <ToggleRow
            icon={XCircle}
            label={t('notification_prefs.host_cancellations')}
            value={prefs.pushHostCancellations}
            onToggle={() => togglePref('pushHostCancellations')}
            disabled={pushOff}
            colors={colors}
            isDark={isDark}
            testID="notifications.hostCancellations-toggle"
          />
          <Divider colors={colors} />
          <ToggleRow
            icon={DollarSign}
            label={t('notification_prefs.host_payouts')}
            value={prefs.pushHostPayouts}
            onToggle={() => togglePref('pushHostPayouts')}
            disabled={pushOff}
            colors={colors}
            isDark={isDark}
            testID="notifications.hostPayouts-toggle"
          />
        </SectionCard>

        <SectionTitle title={t('notification_prefs.section_promotions')} colors={colors} />
        <SectionCard colors={colors}>
          <ToggleRow
            icon={Tag}
            label={t('notification_prefs.promotions')}
            value={prefs.pushPromotions}
            onToggle={() => togglePref('pushPromotions')}
            disabled={pushOff}
            colors={colors}
            isDark={isDark}
            testID="notifications.promotions-toggle"
          />
        </SectionCard>

        <SectionTitle title={t('notification_prefs.section_delivery')} colors={colors} />
        <SectionCard colors={colors}>
          <ToggleRow
            icon={Mail}
            label={t('notification_prefs.email_enabled')}
            value={prefs.emailEnabled}
            onToggle={() => togglePref('emailEnabled')}
            colors={colors}
            isDark={isDark}
            testID="notifications.emailEnabled-toggle"
          />
          <Divider colors={colors} />
          <ToggleRow
            icon={Smartphone}
            label={t('notification_prefs.sms_enabled')}
            value={prefs.smsEnabled}
            onToggle={() => togglePref('smsEnabled')}
            colors={colors}
            isDark={isDark}
            testID="notifications.smsEnabled-toggle"
          />
        </SectionCard>

        <SectionTitle title="Diagnóstico de Notificaciones" colors={colors} />
        <SectionCard colors={colors}>
          <Pressable
            onPress={async () => {
              if (!user?.id || isTestingPush) return
              setIsTestingPush(true)
              try {
                const token = await notificationService.registerPushToken(user.id)
                if (token) {
                  toast.success(`Token registrado: ${token.substring(0, 16)}...`)
                  // Trigger test push via Expo API
                  await fetch('https://exp.host/--/api/v2/push/send', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      to: token,
                      title: '🚗 ¡Prueba Tago!',
                      body: 'Notificación push recibida con éxito en tu dispositivo.',
                      sound: 'default',
                      priority: 'high',
                      channelId: 'default',
                    }),
                  })
                } else {
                  toast.error('No se pudo obtener el token push de este dispositivo.')
                }
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Error al probar notificación')
              } finally {
                setIsTestingPush(false)
              }
            }}
            disabled={isTestingPush}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: SPACING.md,
              paddingHorizontal: SPACING.md,
              minHeight: 48,
            }}
          >
            {isTestingPush ? (
              <ActivityIndicator size="small" color={COLORS.primary} />
            ) : (
              <>
                <BellRing size={20} color={COLORS.primary} strokeWidth={2} />
                <Text
                  style={{
                    marginLeft: SPACING.sm,
                    fontSize: 15,
                    fontWeight: '600',
                    color: COLORS.primary,
                    fontFamily: FONTS.body,
                  }}
                >
                  Probar Notificación Push Ahora
                </Text>
              </>
            )}
          </Pressable>
        </SectionCard>
      </ScrollView>
    </ScreenContainer>
  )
}
